import React from "react";
import { useLocation } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import "./horizonAdmin.css";
import "./horizonDashboard.css";
import "./roleEngineer.css";

export default function AdminPanelLayout({ children }) {
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [pendingExceedanceCount, setPendingExceedanceCount] = React.useState(0);
  const [todayFormCount, setTodayFormCount] = React.useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [formErrorLogsEnabled, setFormErrorLogsEnabled] = React.useState(false);
  const location = useLocation();
  const getBaseUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
  };
  const userName = (function() {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return (user && user.fullName) ? user.fullName : 'Admin';
    } catch {
      return 'Admin';
    }
  })();
  const userRole = (function() {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.role || "";
    } catch {
      return "";
    }
  })();
  const roleLabel = userRole === "subAdmin" ? "Sub Admin" : (userRole ? (userRole[0].toUpperCase() + userRole.slice(1)) : "");
  const isAdmin = userRole === "admin";
  const isEngineerUser = userRole === "engineer";
  const userInitials = userName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "A";
  
  // Function to get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    const titleMap = {
      '/admin/dashboard': 'Main Dashboard',
      '/admin/app-users': 'App Users',
      '/admin/settings': 'Settings',
      '/admin/directors': 'Director',
      '/admin/form-error-log': 'Form Error Logs',
      '/admin/sites': 'Sites',
      '/admin/site-register': 'Site Register',
      '/admin/refusal-to-work-form': 'SF-005 Refusal To Work Form',
      '/admin/ppe-self-check-form': 'SF-006-B PPE Self Check Form',
      '/admin/permit-to-break-ground': 'SF-027-C Permit to Break Ground',
      '/admin/fatigue/d-fatigue-management-plan': 'D-Fatigue Management Plan',
      '/admin/hseq-site-inspection': 'SF-015-B HSEQ Site Inspection',
      '/admin/rail-hseq-site-inspection': 'SF-015-C Rail HSEQ Site Inspection (2)',
      '/admin/ukpn-ppm': 'UKPN PPM',
      '/admin/mewp-checklist': 'MEWP Checklist',
      '/admin/sf-004-g-plant-issue-form': 'SF-004-G Plant Issue Form',
      '/admin/sf-004-i-blue-tower-parts-checklist': 'SF-004-I BLUE Tower Parts Checklist',
      '/admin/sf-004-j-red-tower-parts-checklist': 'SF-004-J RED Tower Parts Checklist',
      '/admin/lser-form': 'LSER Form',
      '/admin/daily-worksheet': 'Daily Worksheet',
      '/admin/work-at-height': 'Work At Height',
      '/admin/ppm-report': 'PPM Report',
      '/admin/accident-report': 'Accident Report',
      '/admin/asbestos-briefing': 'Asbestos Briefing',
      '/admin/hand-arm-vibration': 'Hand Arm Vibration Log',
      '/admin/ukpn-mst': 'UKPN MST',
      '/admin/ukpn-tbs': 'UKPN TBS',
      '/admin/ukpn-reactive': 'UKPN Reactive',
      '/admin/ttsl-daily-worksheet': 'TTSL Daily Worksheet',
      '/admin/form-builder': 'Form Builder',
      '/admin/file-upload': 'File Upload',
      '/admin/calendar': 'Calendar View',
      '/admin/southeastern/outstanding-call': 'Outstanding Call',
      '/admin/southeastern/outstanding-quotes': 'Outstanding Quotes',
      '/admin/southeastern/outstanding-ppms': 'Outstanding PPMs',
      '/admin/southeastern/return-visit': 'Return Visit',
      '/admin/southeastern/new-entry': 'New Entry',
      '/admin/ukpn': 'UKPN',
      '/admin/profile': 'Profile'
    };
    
    // Check for dynamic forms route
    if (path.startsWith('/admin/dynamic-forms/')) {
      return 'Dynamic Forms';
    }
    
    return titleMap[path] || 'Admin Panel';
  };
  
  React.useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.horizon-user-menu') && !e.target.closest('.dropdown-item')) {
        setShowDropdown(false);
      }
      if (!e.target.closest('.horizon-notification') && !e.target.closest('.horizon-notification-dropdown')) {
        setShowNotifications(false);
      }
    };
    if (showDropdown || showNotifications) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown, showNotifications]);

  React.useEffect(() => {
    setFormErrorLogsEnabled(localStorage.getItem("formErrorLogsEnabled") === "true");
    const syncToggle = () => {
      setFormErrorLogsEnabled(localStorage.getItem("formErrorLogsEnabled") === "true");
    };
    window.addEventListener("storage", syncToggle);
    window.addEventListener("formErrorLogsVisibilityChanged", syncToggle);
    return () => {
      window.removeEventListener("storage", syncToggle);
      window.removeEventListener("formErrorLogsVisibilityChanged", syncToggle);
    };
  }, []);

  React.useEffect(() => {
    // Engineers have no fatigue/exceedance visibility and the backend blocks
    // these endpoints for them.
    if (isEngineerUser) return undefined;

    const statusForFilter = (s) => {
      if (s === "approved" || s === "manager_approved") return "approved";
      if (s === "rejected" || s === "manager_rejected") return "rejected";
      return "pending";
    };

    async function fetchPendingExceedanceCount() {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [resA, resB, resC, resD] = await Promise.all([
          fetch(getBaseUrl() + "/api/a-general", { headers }),
          fetch(getBaseUrl() + "/api/b-level1", { headers }),
          fetch(getBaseUrl() + "/api/c-level2", { headers }),
          fetch(getBaseUrl() + "/api/d-fatigue-management-plan", { headers }),
        ]);
        const safeJson = async (res) => {
          try {
            return await res.json();
          } catch {
            return null;
          }
        };
        const [dataA, dataB, dataC, dataD] = await Promise.all([
          safeJson(resA),
          safeJson(resB),
          safeJson(resC),
          safeJson(resD),
        ]);
        const listA = dataA && dataA.success && Array.isArray(dataA.data) ? dataA.data : [];
        const listB = dataB && dataB.success && Array.isArray(dataB.data) ? dataB.data : [];
        const listC = dataC && dataC.success && Array.isArray(dataC.data) ? dataC.data : [];
        const listD = dataD && dataD.success && Array.isArray(dataD.data) ? dataD.data : [];

        const mapped = [
          ...listA.map((item) => ({ ...item, _id: item._id, level: "General" })),
          ...listB.map((item) => ({ ...item, _id: item._id, level: "Level 1" })),
          ...listC.map((item) => ({ ...item, _id: item._id, level: "Level 2" })),
          ...listD.map((item) => ({ ...item, _id: item._id, level: "Plan" })),
        ];

        const pendingCount = mapped.filter((f) => statusForFilter(f?.status) === "pending").length;
        setPendingExceedanceCount(pendingCount);
      } catch {
        setPendingExceedanceCount(0);
      }
    }
    fetchPendingExceedanceCount();
    const onUpdated = () => fetchPendingExceedanceCount();
    window.addEventListener("exceedanceFormsUpdated", onUpdated);
    const id = setInterval(fetchPendingExceedanceCount, 60000);
    return () => {
      clearInterval(id);
      window.removeEventListener("exceedanceFormsUpdated", onUpdated);
    };
  }, [isEngineerUser]);

  React.useEffect(() => {
    if (!isAdmin) return undefined;
    // Notification behaviour: badge counts only submissions created AFTER the last
    // time the user opened the Form Submissions page (stored in localStorage). After
    // clicking the icon, "seen at" is set to now, so the badge clears until a new form.
    async function fetchUnseenFormCount() {
      try {
        const token = localStorage.getItem("token");
        let seenAt = localStorage.getItem("formSubmissionsSeenAt");
        if (!seenAt) {
          seenAt = new Date().toISOString();
          localStorage.setItem("formSubmissionsSeenAt", seenAt);
        }
        const res = await fetch(`${getBaseUrl()}/api/form-submissions?fromDate=${encodeURIComponent(seenAt)}&limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.success) setTodayFormCount(Number(data.meta?.total || 0));
        else setTodayFormCount(0);
      } catch {
        setTodayFormCount(0);
      }
    }
    fetchUnseenFormCount();
    const id = setInterval(fetchUnseenFormCount, 60000);
    return () => clearInterval(id);
  }, [isAdmin]);

  const handleToggleFormErrorLogs = () => {
    if (!isAdmin) return;
    const next = !formErrorLogsEnabled;
    setFormErrorLogsEnabled(next);
    localStorage.setItem("formErrorLogsEnabled", String(next));
    window.dispatchEvent(new Event("formErrorLogsVisibilityChanged"));
  };

  return (
    <div className={`horizon-admin-layout ${isEngineerUser ? 'role-engineer' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${location.pathname.startsWith('/admin/southeastern/') && location.pathname !== '/admin/southeastern/new-entry' ? 'no-max-height' : ''}`}>
      <AdminSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className="horizon-main-content">
        {/* Header */}
        <header className="horizon-header">
          <div className="horizon-header-left">
            <button 
              className="sidebar-toggle-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}
            >
              <i className={`fa-solid fa-chevron-${sidebarCollapsed ? 'right' : 'left'}`}></i>
            </button>
            {/* <div className="horizon-header-title">Pages / {getPageTitle()}</div> */}
            <h1 className="horizon-header-main">{getPageTitle()}</h1>
          </div>
              <div className="horizon-header-right">
                {isAdmin ? (
                  <div className="header-form-logs-toggle-wrap">
                    <span>Form Logs</span>
                    <button
                      type="button"
                      className={`header-form-logs-toggle ${formErrorLogsEnabled ? "on" : ""}`}
                      onClick={handleToggleFormErrorLogs}
                      aria-label="Toggle Form Error Logs visibility"
                    >
                      <span />
                    </button>
                  </div>
                ) : null}
                {isAdmin ? (
                  <div className="horizon-notification">
                    <button
                      type="button"
                      className="horizon-header-btn"
                      aria-label="Form Submissions"
                      title="Form Submissions"
                      onClick={() => {
                        localStorage.setItem("formSubmissionsSeenAt", new Date().toISOString());
                        setTodayFormCount(0);
                        window.location.href = "/admin/form-submissions";
                      }}
                    >
                      <i className="fa-solid fa-file-pen"></i>
                      {todayFormCount > 0 && (
                        <span className="horizon-notification-badge" aria-label={`${todayFormCount} forms filled today`}>
                          {todayFormCount > 99 ? "99+" : todayFormCount}
                        </span>
                      )}
                    </button>
                  </div>
                ) : null}
                {!isEngineerUser && (
                <div className="horizon-notification">
                  <button
                    type="button"
                    className="horizon-header-btn"
                    aria-label="Notifications"
                    aria-haspopup="menu"
                    aria-expanded={showNotifications}
                    onClick={() => setShowNotifications(v => !v)}
                  >
                    <i className="fa-regular fa-bell"></i>
                    {pendingExceedanceCount > 0 && (
                      <span className="horizon-notification-badge" aria-label={`${pendingExceedanceCount} pending exceedance forms`}>
                        {pendingExceedanceCount > 99 ? "99+" : pendingExceedanceCount}
                      </span>
                    )}
                  </button>
                  {showNotifications && (
                    <div className="horizon-notification-dropdown" role="menu">
                      <div className="horizon-notification-title">Notifications</div>
                      {pendingExceedanceCount === 0 ? (
                        <div className="horizon-notification-empty">No new notifications</div>
                      ) : (
                        <button
                          type="button"
                          className="horizon-notification-item"
                          onClick={() => {
                            setShowNotifications(false);
                            window.location.href = "/admin/fatigue/exceedance-forms";
                          }}
                        >
                          <div className="horizon-notification-item-left">
                            <span className="horizon-notification-dot" />
                            <div>
                              <div className="horizon-notification-item-main">Exceedance Forms</div>
                              <div className="horizon-notification-item-sub">{pendingExceedanceCount} pending</div>
                            </div>
                          </div>
                          <i className="fa-solid fa-chevron-right horizon-notification-item-chevron" aria-hidden="true"></i>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                )}
                <div className="horizon-user-menu">
                  <button
                    type="button"
                    className={`horizon-user-trigger${showDropdown ? " is-open" : ""}`}
                    onClick={() => setShowDropdown(v => !v)}
                    aria-haspopup="menu"
                    aria-expanded={showDropdown}
                  >
                    <span className="horizon-user-avatar" aria-hidden="true">{userInitials}</span>
                    <span className="horizon-user-name" title={userName}>
                      {userName}{roleLabel ? ` (${roleLabel})` : ""}
                    </span>
                    <i className="fa-solid fa-chevron-down horizon-user-chevron" aria-hidden="true"></i>
                  </button>
                  {showDropdown && (
                    <div className="horizon-user-dropdown" role="menu">
                      <button
                        type="button"
                        className="horizon-user-item dropdown-item"
                        onClick={() => {
                          setShowDropdown(false);
                          window.location.href = '/admin/profile';
                        }}
                      >
                        <i className="fa-solid fa-user"></i>
                        <span>Profile</span>
                      </button>
                      <button
                        type="button"
                        className="horizon-user-item dropdown-item"
                        onClick={() => {
                          localStorage.removeItem('token');
                          setShowDropdown(false);
                          window.location.href = '/admin';
                        }}
                      >
                        <i className="fa-solid fa-right-from-bracket"></i>
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
        </header>
        <main className="horizon-dashboard-content">
          {children}
        </main>
      </div>
    </div>
  );
}
